import jStat from 'jstat';
import type { ANOVAParams } from './types';

/**
 * Performs one-way ANOVA test
 * @param params Object containing array of groups
 * @returns Object containing F-statistic, p-value, and other ANOVA statistics
 */
export function calculateOneWayANOVA({ groups }: ANOVAParams): {
  fStatistic: number;
  pValue: number;
  dfBetween: number;
  dfWithin: number;
  ssBetween: number;
  ssWithin: number;
  msBetween: number;
  msWithin: number;
  groupMeans: number[];
  groupSizes: number[];
} {
  if (groups.length < 2) throw new Error('At least two groups are required for ANOVA');
  if (groups.some(group => group.length === 0)) throw new Error('Empty groups are not allowed');
  
  // Calculate group means and sizes
  const groupMeans = groups.map(group => jStat.mean(group));
  const groupSizes = groups.map(group => group.length);
  const allValues = groups.flat();
  const overallMean = jStat.mean(allValues);
  
  // Calculate degrees of freedom
  const dfBetween = groups.length - 1;
  const dfWithin = allValues.length - groups.length;
  
  // Calculate sum of squares between groups
  const ssBetween = groups.reduce((sum, group, i) => {
    return sum + group.length * Math.pow(groupMeans[i]! - overallMean, 2);
  }, 0);
  
  // Calculate sum of squares within groups using jStat's sumsqerr
  const ssWithin = groups.reduce((sum, group, i) => {
    const groupMean = groupMeans[i]!;
    const deviations = group.map(value => value - groupMean);
    return sum + jStat.sumsqerr(deviations);
  }, 0);
  
  // Calculate mean squares
  const msBetween = ssBetween / dfBetween;
  const msWithin = ssWithin / dfWithin || 0; // Handle case where all values in each group are identical
  
  // Calculate F-statistic
  const fStatistic = msWithin === 0 ? 0 : msBetween / msWithin;
  
  // Calculate p-value using F-test
  const pValue = msWithin === 0 ? 1 : jStat.ftest(fStatistic, dfBetween, dfWithin);
  
  return {
    fStatistic,
    pValue,
    dfBetween,
    dfWithin,
    ssBetween,
    ssWithin,
    msBetween,
    msWithin,
    groupMeans,
    groupSizes
  };
} 